import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { isNil, upperCase } from 'lodash-es';

import { useInternational } from '@/common/locale';
import { useMessage } from '@/common/inject/useMessage';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { useVueQueryErrorHandler } from '@/common/composables/useVueQueryErrorHandler';

import {
  useMenuSearchPermission,
  useFavoriteDashboardsMutation,
} from '@/common/components/organisms/menuSearch/menuSearch.service';

import {
  DashboardType,
  MenuSearchResultItem,
} from '@/common/components/organisms/menuSearch/menuSearch.type';
import {
  DASHBOARD_TYPE,
  DASHBOARD_PERMISSION_CODE,
  DASHBOARD_NOT_PERMISSION_CODE_LIST,
} from '@/common/components/organisms/menuSearch/menuSearch.define';
import {
  useSharedMenuSearchState,
  useMenuSearchFilter,
  useSharedMenuSearchData,
} from '@/common/components/organisms/menuSearch/menuSearch.uses';

export const setup = () => {
  const router = useRouter();
  const { isShowMenuSearch } = storeToRefs(useBaseMenuStore());

  const { t } = useInternational();
  const { showErrorMsg } = useMessage();
  const { errorHandlerWithMessage } = useVueQueryErrorHandler();

  const sharedState = useSharedMenuSearchState();
  const { groupedSearchResults } = useMenuSearchFilter(undefined, sharedState);
  const { isDashboardListError } = useSharedMenuSearchData();
  const { updateFavoriteDashboard } = useFavoriteDashboardsMutation();
  const { getDashboardPermission } = useMenuSearchPermission();

  const getDashboardType = (dashboardType: DashboardType) => {
    return t(DASHBOARD_TYPE[upperCase(dashboardType)]);
  };

  const handleClickFavorite = async ({
    dashboardId,
    favoriteId,
  }: {
    dashboardId?: number;
    favoriteId?: number;
  }) => {
    if (!dashboardId || !favoriteId) {
      return;
    }

    try {
      await updateFavoriteDashboard({ dashboardId, favoriteId });
    } catch (error) {
      errorHandlerWithMessage({ error });
    }
  };

  const handleClickDashboard = async (item: MenuSearchResultItem) => {
    const { dashboardId, path } = item;

    if (!dashboardId || !path) {
      return;
    }

    try {
      const permissionCode = await getDashboardPermission(dashboardId);

      if (isNil(permissionCode)) {
        return;
      }

      if (DASHBOARD_NOT_PERMISSION_CODE_LIST.includes(permissionCode)) {
        showErrorMsg({
          msgStr: t(DASHBOARD_PERMISSION_CODE[permissionCode]),
        });

        return;
      }

      router.push(path);
      isShowMenuSearch.value = false;
    } catch (error) {
      errorHandlerWithMessage({ error });
      return;
    }
  };

  const handleClick = async (item: MenuSearchResultItem) => {
    const { path, type } = item;

    if (!path) {
      return;
    }

    switch (type) {
      case 'menu':
        router.push(path);
        isShowMenuSearch.value = false;
        break;
      case 'dashboard':
        await handleClickDashboard(item);
        break;
      default:
        return;
    }
  };

  return {
    t,
    groupedSearchResults,
    isDashboardListError,

    getDashboardType,
    handleClick,
    handleClickFavorite,
  };
};
